/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
/**
 * AI Import Modal — Intelligent Excel/CSV Breakdown Importer
 *
 * 4-step wizard:
 *   1. Upload  — drag & drop file
 *   2. Analyze — AI parsing animation + mapping preview
 *   3. Confirm — editable column mappings with confidence scores
 *   4. Summary — import results
 */
import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  X, Sparkles, CheckCircle2, AlertTriangle,
  FileSpreadsheet, Zap, ArrowRight, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Target field options (for manual override dropdown) ──────────────────────
const TARGET_FIELDS = [
  { value: 'IGNORE',           label: '— Skip Column —',        group: '' },
  { value: 'scene_number',     label: 'Scene Number',            group: 'Scene' },
  { value: 'scene_header',     label: 'Scene Header (Full)',     group: 'Scene' },
  { value: 'location_type',    label: 'INT / EXT',               group: 'Scene' },
  { value: 'time_of_day',      label: 'Day / Night',             group: 'Scene' },
  { value: 'location_name',    label: 'Location Name',           group: 'Scene' },
  { value: 'synopsis',         label: 'Synopsis / Description',  group: 'Scene' },
  { value: 'characters',       label: 'Cast / Characters',       group: 'Scene' },
  { value: 'pages',            label: 'Page Count',              group: 'Scene' },
  { value: 'PROP',             label: 'Props',                   group: 'Breakdown' },
  { value: 'COSTUME',          label: 'Costume / Wardrobe',      group: 'Breakdown' },
  { value: 'ART_SET_DRESSING', label: 'Art / Set Dressing',      group: 'Breakdown' },
  { value: 'VFX',              label: 'VFX',                     group: 'Breakdown' },
  { value: 'SFX',              label: 'SFX / Practical FX',      group: 'Breakdown' },
  { value: 'STUNT',            label: 'Stunts',                  group: 'Breakdown' },
  { value: 'MAKEUP_HAIR',      label: 'Makeup / Hair',           group: 'Breakdown' },
  { value: 'PICTURE_VEHICLE',  label: 'Picture Vehicles',        group: 'Breakdown' },
  { value: 'ANIMAL',           label: 'Animals',                 group: 'Breakdown' },
  { value: 'EXTRA_BACKGROUND', label: 'Extras / Background',     group: 'Breakdown' },
  { value: 'SOUND_MUSIC',      label: 'Sound / Music',           group: 'Breakdown' },
  { value: 'PRODUCTION_NOTE',  label: 'Production Notes',        group: 'Breakdown' },
];

const CONF_COLOR = (c) => {
  if (c >= 0.9) return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25';
  if (c >= 0.75) return 'bg-blue-500/15 text-blue-400 border-blue-500/25';
  if (c >= 0.6)  return 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25';
  return 'bg-red-500/15 text-red-400 border-red-500/25';
};

const CONF_LABEL = (c) => {
  if (c >= 0.9) return 'High';
  if (c >= 0.75) return 'Good';
  if (c >= 0.6)  return 'Low';
  return 'Guess';
};

// ── AI Analysis animation ─────────────────────────────────────────────────────
const AIAnimating = ({ filename }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-6">
    <div className="relative">
      <div className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(232,197,71,0.12)', border: '1px solid rgba(232,197,71,0.2)' }}>
        <Sparkles className="h-7 w-7 text-[#e8c547]" />
      </div>
      <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#e8c547] animate-pulse" />
    </div>
    <div className="text-center">
      <p className="text-[#f0f0f0] font-medium mb-1">AI Analyzing Breakdown</p>
      <p className="text-[#606060] text-sm">{filename}</p>
    </div>
    <div className="space-y-2 text-left w-72">
      {[
        'Reading spreadsheet structure…',
        'Detecting column patterns…',
        'Applying production knowledge…',
        'Mapping to breakdown schema…',
      ].map((step, i) => (
        <div key={step} className="flex items-center gap-2.5 animate-pulse" style={{ animationDelay: `${i * 0.4}s` }}>
          <div className="h-1.5 w-1.5 rounded-full bg-[#e8c547]" />
          <span className="text-xs text-[#888]">{step}</span>
        </div>
      ))}
    </div>
  </div>
);

// ── Drag & Drop Upload ────────────────────────────────────────────────────────
const DropZone = ({ onFile }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }, [onFile]);

  return (
    <div
      className="rounded-2xl border-2 border-dashed transition-all duration-150 cursor-pointer p-12 flex flex-col items-center gap-4"
      style={{
        borderColor: dragging ? '#e8c547' : '#2a2a2a',
        background: dragging ? 'rgba(232,197,71,0.04)' : '#111',
      }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
    >
      <div className="h-14 w-14 rounded-xl flex items-center justify-center" style={{ background: 'rgba(232,197,71,0.08)' }}>
        <FileSpreadsheet className="h-6 w-6 text-[#e8c547]" />
      </div>
      <div className="text-center">
        <p className="text-[#f0f0f0] font-medium mb-1">Drop your breakdown file here</p>
        <p className="text-[#666] text-sm">Excel (.xlsx, .xls) or CSV — any format, any studio</p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        {['Movie Magic', 'StudioBinder', 'Custom Excel', 'Google Sheets Export', 'Manual AD Sheets'].map(f => (
          <span key={f} className="text-[10px] px-2 py-0.5 rounded-full text-[#555]" style={{ border: '1px solid #2a2a2a' }}>{f}</span>
        ))}
      </div>
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
             onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
    </div>
  );
};

// ── Mapping preview table ─────────────────────────────────────────────────────
const MappingTable = ({ headers, mappings, sampleRows, onUpdate }) => (
  <div className="overflow-y-auto max-h-[400px] rounded-xl border border-[#2a2a2a]">
    <table className="w-full text-sm">
      <thead className="sticky top-0" style={{ background: 'var(--cb-surface)' }}>
        <tr>
          <th className="text-left p-3 text-[#555] text-xs uppercase tracking-wider font-semibold">Excel Column</th>
          <th className="text-left p-3 text-[#555] text-xs uppercase tracking-wider font-semibold">Sample Data</th>
          <th className="text-left p-3 text-[#555] text-xs uppercase tracking-wider font-semibold">Maps To</th>
          <th className="text-left p-3 text-[#555] text-xs uppercase tracking-wider font-semibold">Confidence</th>
        </tr>
      </thead>
      <tbody>
        {headers.map((header, i) => {
          const mapping = mappings[header] || { field: 'IGNORE', confidence: 0.5 };
          const sample = sampleRows.slice(0, 2).map(r => r[i]).filter(Boolean).join(' | ');
          const fieldLabel = TARGET_FIELDS.find(f => f.value === mapping.field)?.label || mapping.field;
          return (
            <tr key={header} className="border-t border-[#1e1e1e] hover:bg-[#1a1a1a] transition-colors">
              <td className="p-3">
                <span className="font-medium text-[#f0f0f0]">{header}</span>
              </td>
              <td className="p-3">
                <span className="text-[11px] text-[#666] truncate block max-w-[160px]">{sample || '—'}</span>
              </td>
              <td className="p-3">
                <select
                  value={mapping.field}
                  onChange={e => onUpdate(header, e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-lg outline-none w-full"
                  style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', color: mapping.field === 'IGNORE' ? 'var(--cb-text-dim)' : 'var(--cb-text)' }}
                  data-testid={`mapping-select-${i}`}
                >
                  {TARGET_FIELDS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                {mapping.reason && (
                  <p className="text-[9px] text-[#444] mt-0.5 truncate">{mapping.reason}</p>
                )}
              </td>
              <td className="p-3">
                {mapping.field !== 'IGNORE' && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${CONF_COLOR(mapping.confidence)}`}>
                    {CONF_LABEL(mapping.confidence)} {Math.round(mapping.confidence * 100)}%
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ── Import Summary ────────────────────────────────────────────────────────────
const ImportSummary = ({ summary, onClose, onImportComplete }) => (
  <div className="py-6 space-y-6">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
      </div>
      <div>
        <p className="text-[#f0f0f0] font-semibold">Import Complete</p>
        <p className="text-[#666] text-sm">Your breakdown has been imported successfully</p>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3">
      {[
        { v: summary.scenes_created,  l: 'Scenes Created',  c: 'emerald' },
        { v: summary.scenes_updated,  l: 'Scenes Updated',  c: 'blue'    },
        { v: summary.items_created,   l: 'Items Imported',  c: 'purple'  },
      ].map(({ v, l, c }) => (
        <div key={l} className="rounded-xl p-4 text-center" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
          <div className="text-2xl font-bold text-[#f0f0f0] mb-1">{v ?? 0}</div>
          <div className="text-xs text-[#666]">{l}</div>
        </div>
      ))}
    </div>

    {summary.columns_used && Object.keys(summary.columns_used).length > 0 && (
      <div>
        <p className="text-xs text-[#555] uppercase tracking-wider mb-2 font-semibold">Categories Imported</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(summary.columns_used).map(([field, count]) => (
            <span key={field} className="text-[10px] px-2 py-0.5 rounded-full bg-[#1a1a1a] text-[#a0a0a0] border border-[#2a2a2a]">
              {field} ({count})
            </span>
          ))}
        </div>
      </div>
    )}

    {summary.conflicts?.length > 0 && (
      <div className="rounded-xl p-4 space-y-1.5" style={{ background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.15)' }}>
        <p className="text-xs font-semibold text-[#FB923C] mb-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> Conflicts ({summary.conflicts.length})
        </p>
        {summary.conflicts.slice(0, 5).map((c, i) => (
          <p key={i} className="text-[11px] text-[#a0a0a0]">{c}</p>
        ))}
        {summary.conflicts.length > 5 && (
          <p className="text-[10px] text-[#666]">+{summary.conflicts.length - 5} more</p>
        )}
      </div>
    )}

    <button
      onClick={() => { onImportComplete?.(); onClose(); }}
      className="w-full py-3 rounded-xl bg-[#e8c547] hover:bg-[#d6b539] text-black font-semibold text-sm transition-all"
    >
      View Breakdown
    </button>
  </div>
);

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function AIImportModal({ projectId, token, onClose, onImportComplete }) {
  const [step, setStep] = useState('upload'); // upload | analyzing | mapping | applying | summary
  const [file, setFile] = useState(null);
  const [fileB64, setFileB64] = useState('');
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [mappings, setMappings] = useState({});
  const [summary, setSummary] = useState(null);

  const handleFile = async (f) => {
    setFile(f);
    setStep('analyzing');

    // Convert to base64 for later apply step
    const reader = new FileReader();
    reader.onload = (e) => {
      const b64 = e.target.result.split(',')[1];
      setFileB64(b64);
    };
    reader.readAsDataURL(f);

    // Send to AI analyze endpoint
    const formData = new FormData();
    formData.append('file', f);
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/import-excel-ai/analyze`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setAnalyzeResult(data);
      // Init mappings from AI result
      const initMappings = {};
      data.headers.forEach(h => {
        initMappings[h] = data.mappings[h] || { field: 'IGNORE', confidence: 0.5, reason: '' };
      });
      setMappings(initMappings);
      setStep('mapping');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI analysis failed');
      setStep('upload');
    }
  };

  const handleApply = async () => {
    if (!fileB64) return;
    setStep('applying');
    const confirmedMappings = {};
    Object.entries(mappings).forEach(([col, m]) => {
      confirmedMappings[col] = m.field;
    });
    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/import-excel-ai/apply`,
        {
          file_b64: fileB64,
          filename: file.name,
          sheet_index: 0,
          confirmed_mappings: confirmedMappings,
          header_row: analyzeResult?.detected_header_row ?? 0,
          detected_header_row: analyzeResult?.detected_header_row ?? 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(data);
      setStep('summary');
      // Refresh breakdown whenever something was created OR updated OR items added
      if ((data.scenes_created || 0) + (data.scenes_updated || 0) + (data.items_created || 0) > 0) {
        onImportComplete?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setStep('mapping');
    }
  };

  const updateMapping = (col, newField) => {
    setMappings(prev => ({
      ...prev,
      [col]: { ...prev[col], field: newField, confidence: 1.0, reason: 'Manual override' },
    }));
  };

  const mappedCount = Object.values(mappings).filter(m => m.field !== 'IGNORE').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e1e1e] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-[#e8c547]/10">
              <Sparkles className="h-4 w-4 text-[#e8c547]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#f0f0f0]">AI Breakdown Import</h2>
              <p className="text-[10px] text-[#555]">Intelligent column detection — any format</p>
            </div>
          </div>
          {/* Step indicator */}
          <div className="flex items-center gap-2">
            {['upload', 'mapping', 'summary'].map((s, i) => (
              <div key={s} className="flex items-center gap-1.5">
                {i > 0 && <div className="w-4 h-px bg-[#2a2a2a]" />}
                <div className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  step === s || (step === 'analyzing' && s === 'upload') || (step === 'applying' && s === 'mapping')
                    ? 'bg-[#e8c547]'
                    : step === 'summary' || (step === 'mapping' && i < 1) || (step === 'applying' && i < 2) ? 'bg-emerald-500/50'
                    : 'bg-[#2a2a2a]'
                }`} />
              </div>
            ))}
          </div>
          <button onClick={onClose} className="text-[#555] hover:text-[#f0f0f0] transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

          {step === 'upload' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-[#f0f0f0] font-semibold mb-1">Import Breakdown File</h3>
                <p className="text-[#666] text-sm">AI will automatically detect column types and map them to our production schema — no templates needed.</p>
              </div>
              <DropZone onFile={handleFile} />
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.12)' }}>
                <Info className="h-3.5 w-3.5 text-[#4a9eff] mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-[#4a9eff]">
                  Works with Movie Magic, StudioBinder, and any custom Excel breakdown format. AI understands production shorthand: "Wardrobe" → Costume, "Set Dec" → Art/Set Dressing, "BG" → Extras, etc.
                </p>
              </div>
            </div>
          )}

          {(step === 'analyzing' || step === 'applying') && (
            <AIAnimating filename={file?.name || ''} />
          )}

          {step === 'mapping' && analyzeResult && (
            <div className="space-y-4">
              {/* Stats row */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-[#e8c547]" />
                  <span className="text-sm text-[#f0f0f0] font-medium">{file?.name}</span>
                </div>
                <span className="text-xs text-[#555]">{analyzeResult.total_data_rows} rows</span>
                <span className="text-xs text-[#555]">{analyzeResult.headers.length} columns</span>
                <span className="text-xs text-[#555]">{mappedCount} mapped</span>
              </div>

              {/* AI suggestions */}
              {analyzeResult.suggestions?.length > 0 && (
                <div className="rounded-xl p-3 space-y-1" style={{ background: 'rgba(232,197,71,0.06)', border: '1px solid rgba(232,197,71,0.12)' }}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#e8c547] mb-2 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> AI Insights
                  </p>
                  {analyzeResult.suggestions.map((s, i) => (
                    <p key={i} className="text-[11px] text-[#a0a0a0]">• {s}</p>
                  ))}
                </div>
              )}

              {/* Mapping table */}
              <div>
                <p className="text-xs text-[#555] uppercase tracking-wider mb-2 font-semibold">Column Mapping — Review & Edit</p>
                <MappingTable
                  headers={analyzeResult.headers}
                  mappings={mappings}
                  sampleRows={analyzeResult.sample_rows}
                  onUpdate={updateMapping}
                />
              </div>

              {analyzeResult.issues?.length > 0 && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.12)' }}>
                  <p className="text-[10px] font-bold text-[#F87171] mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="h-3 w-3" /> Issues Detected
                  </p>
                  {analyzeResult.issues.map((iss, i) => (
                    <p key={i} className="text-[11px] text-[#a0a0a0]">• {iss}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'summary' && summary && (
            <ImportSummary summary={summary} onClose={onClose} onImportComplete={onImportComplete} />
          )}
        </div>

        {/* Footer buttons */}
        {step === 'mapping' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#1e1e1e] flex-shrink-0">
            <button
              onClick={() => setStep('upload')}
              className="text-[#555] hover:text-[#f0f0f0] text-sm transition-colors flex items-center gap-1.5"
            >
              ← Upload different file
            </button>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-[#555]">
                {mappedCount} of {analyzeResult?.headers?.length || 0} columns mapped
              </span>
              <button
                onClick={handleApply}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#e8c547] hover:bg-[#d6b539] text-black font-semibold text-sm transition-all"
                data-testid="confirm-ai-import-btn"
              >
                <Zap className="h-4 w-4" /> Apply Import
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
